<template>
  <CardWrapper>
    <!-- <h2 class="title md">Player Rankings</h2> -->
    <div v-ga-section-view data-section-name="Control Center" data-section-number="1">
      <div class="header-bar">
        <h3>CONTROL CENTER</h3>
        <RankingSearchInput
          v-if="!mobile"
          class="search-box"
          :model-value="searchKeyword"
          :type="searchType"
          @update:model-value="search(searchType, $event)"
          @update:type="search($event, '')"
        />
        <i-local-search v-else class="search-button-mobile" @click="isMobileSearchMode = true" />
      </div>
      <div class="ranking-options-layout">
        <div class="font2 md">
          Region
          <div
            v-if="mobile"
            class="dropdown"
            :class="{ expanded: expandedMenu == 'region' }"
            @click="expandedMenu = 'region'"
          >
            <RankingOption v-if="rankingRegion" :item="rankingRegion" :model-value="rankingRegion" />
            <i-local-home-banner-arrow class="dropdown-icon" />
          </div>
        </div>
        <DropDownElement :disable="!mobile" :expanded="expandedMenu == 'region'">
          <div class="options">
            <RankingOption
              v-for="one in rankingRegions"
              :key="one.key"
              :item="one"
              :model-value="rankingRegion"
              @update:model-value="updateRegion($event)"
            />
          </div>
        </DropDownElement>
        <div class="font2 md">
          Ranking Type
          <div
            v-if="mobile"
            class="dropdown"
            :class="{ expanded: expandedMenu == 'type' }"
            @click="expandedMenu = 'type'"
          >
            <RankingOption v-if="rankingType" :item="rankingType" :model-value="rankingType" />
            <i-local-home-banner-arrow class="dropdown-icon" />
          </div>
        </div>
        <DropDownElement :disable="!mobile" :expanded="expandedMenu == 'type'">
          <div class="options">
            <RankingOption
              v-for="one in rankingTypes"
              :key="one.key"
              :disabled="!rankingRegion"
              :item="one"
              :model-value="rankingType"
              @update:model-value="updateType($event)"
            />
          </div>
        </DropDownElement>

        <template v-if="availableRankingFiltersForMobile.length > 0">
          <div class="font2 md">
            {{
              mobile && availableRankingFiltersForMobile[0].kind ? availableRankingFiltersForMobile[0].kind : 'Filter'
            }}
            <div
              v-if="mobile"
              class="dropdown"
              :class="{ expanded: expandedMenu == 'filter' }"
              @click="expandedMenu = 'filter'"
            >
              <RankingOption v-if="rankingFilter" :item="rankingFilter" :model-value="rankingFilter" />
              <i-local-home-banner-arrow class="dropdown-icon" />
            </div>
          </div>
          <DropDownElement :disable="!mobile" :expanded="expandedMenu == 'filter'">
            <div class="options">
              <RankingOption
                v-for="one in mobile ? availableRankingFiltersForMobile : rankingFilters"
                :key="one.key"
                :absolute-dropdown="!mobile"
                :disabled="!mobile && (!rankingType || !one.dependancies?.includes(rankingType.key))"
                :filter="one.key === 'job' ? rankingFilter?.key : one.key === 'world' ? String(rankingRegion?.id) : ''"
                :item="one"
                :model-value="!mobile && one.key === 'job' ? rankingJob : rankingFilter"
                @update:model-value="one.key === 'job' ? updateFilter2($event) : updateFilter($event)"
              />
            </div>
          </DropDownElement>
        </template>

        <template v-if="mobile && rankingFilter && rankingFilter.kind == 'faction'">
          <div class="font2 md">
            JOB
            <div
              v-if="mobile"
              class="dropdown"
              :class="{ expanded: expandedMenu == 'job' }"
              @click="expandedMenu = 'job'"
            >
              <RankingOption v-if="rankingJob" :item="rankingJob" :model-value="rankingJob" />
              <i-local-home-banner-arrow class="dropdown-icon" />
            </div>
          </div>
          <DropDownElement :disable="!mobile" :expanded="expandedMenu == 'job'">
            <div class="options">
              <RankingOption
                v-for="one in rankingJobs"
                :key="one.key"
                :item="one"
                :model-value="rankingJob"
                @update:model-value="updateFilter2($event)"
              />
            </div>
          </DropDownElement>
        </template>

        <template v-if="!disabledWorldType">
          <div class="font2 md">
            WORLD TYPE
            <div
              v-if="mobile"
              class="dropdown"
              :class="{ expanded: expandedMenu == 'world' }"
              @click="expandedMenu = 'world'"
            >
              <RankingOption v-if="rankingWorldType" :item="rankingWorldType" :model-value="rankingWorldType" />
              <i-local-home-banner-arrow class="dropdown-icon" />
            </div>
          </div>
          <DropDownElement :disable="!mobile" :expanded="expandedMenu == 'world'">
            <div class="options">
              <RankingOption
                v-for="one in worldTypes"
                :key="one.key"
                :disabled="disabledWorldType"
                :item="one"
                :model-value="rankingWorldType"
                @update:model-value="updateWorldType(one)"
              />
            </div>
          </DropDownElement>
        </template>
        <div v-if="mobile" class="font2 md mobile-reset" @click="reset">RESET</div>
      </div>
    </div>

    <div class="header-bar rankings-header">
      <h3>RANKINGS</h3>
    </div>
    <div v-if="!optionSelectionComplete" class="no-option-complete">
      To view the rankings, please choose the filter options above.
    </div>
    <RankingList v-else />
  </CardWrapper>
  <SearchBoxMobile
    v-model="isMobileSearchMode"
    :input-value="searchKeyword"
    :placeholder="'Character Name'"
    @update:search="search('character-name', $event)"
  />
</template>

<script setup lang="ts">
  import { rankingFilters, rankingOptionI, rankingRegions, rankingTypes, worldTypes } from '@/config/rankings'
  import { useRankingOptionsStore } from '@/store/rankings/RankingOptions'
  import { storeToRefs } from 'pinia'
  import RankingList from './RankingList.vue'
  import { useGAEventStore } from '@/store/GAEventTracking'
  import { RouteLocationRaw } from 'vue-router'
  const router = useRouter()
  const route = useRoute()

  const gaStore = useGAEventStore()

  const { mobile, smAndDown: tabletAndMobile } = useDisplay()
  const {
    rankingRegion,
    rankingType,
    rankingFilter,
    rankingJobs,
    rankingJob,
    rankingWorldType,
    disabledWorldType,
    optionSelectionComplete,
    searchKeyword,
    searchType,
  } = storeToRefs(useRankingOptionsStore())

  const expandedMenu = ref('region') // only for mobile

  const availableRankingFiltersForMobile = computed(() => {
    const filters = rankingFilters.filter(
      (x) => x.key !== 'job' && rankingType.value && x.dependancies?.includes(rankingType.value.key)
    )
    return filters.length === 1
      ? filters[0].children?.filter((x) => !x.filter || x.filter === rankingRegion.value?.id) ?? [] // filter for world list
      : filters
  })

  function gaEvent(content: string) {
    gaStore.pushActionEvent({
      sectionNumber: 1,
      sectionName: 'Control Center',
      interaction: 'filter_select',
      sectionContent: content,
    })
  }

  // change route first and then, update value
  function updateRegion(region: rankingOptionI | undefined, func: (to: RouteLocationRaw) => void = router.push) {
    if (!optionSelectionComplete.value) func = router.replace
    func({
      params: { region: region?.key, ...(mobile.value ? { type: '', filter: '', filter2: '' } : {}) },
      query: route.query,
    })
    if (region) {
      localStorage.setItem('MS/region', region.key)
      gaEvent(`Region: ${region.name}`)
    }
    if (mobile.value) expandedMenu.value = 'type'
  }

  function updateType(type: rankingOptionI | undefined, func: (to: RouteLocationRaw) => void = router.push) {
    if (!optionSelectionComplete.value) func = router.replace
    func({ params: { type: type ? `${type.key}-ranking` : '', filter: '', filter2: '' } })
    if (mobile.value) expandedMenu.value = 'filter'
    if (type) {
      gaEvent(`Type: ${type.name}`)
    }
  }

  function updateFilter(filter: rankingOptionI | undefined, func: (to: RouteLocationRaw) => void = router.push) {
    if (!optionSelectionComplete.value) func = router.replace
    func({ params: { filter: filter?.key ?? '', filter2: '' } })
    if (mobile.value) expandedMenu.value = filter?.kind === 'faction' ? 'job' : 'world'
    if (filter) {
      gaEvent(`Filter: ${filter.name}`)
    }
  }

  function updateFilter2(filter2: rankingOptionI | undefined, func: (to: RouteLocationRaw) => void = router.push) {
    if (!optionSelectionComplete.value) func = router.replace
    func({ params: { filter2: filter2?.key ?? '' } })
    if (mobile.value) expandedMenu.value = 'world'
    if (filter2) {
      gaEvent(`Filter: ${filter2.name}`)
    }
  }

  function updateWorldType(type: rankingOptionI | undefined, func: (to: RouteLocationRaw) => void = router.push) {
    if (!optionSelectionComplete.value) func = router.replace
    func({ query: { world_type: type?.key } })
    if (mobile.value) expandedMenu.value = 'reset'
    if (type) {
      gaEvent(`World: ${type.name}`)
    }
  }

  function search(type: string, keyword: string, func: (to: RouteLocationRaw) => void = router.push) {
    if (!optionSelectionComplete.value || !keyword) func = router.replace
    const query = {
      ...route.query,
      search_type: type,
      search: keyword,
    }
    func({ query })
    if (keyword) {
      gaStore.pushActionEvent({
        sectionNumber: 1,
        sectionName: 'Control Center',
        interaction: 'search',
        sectionContent: type === 'ranks' ? 'Rank' : 'Chracter Name',
      })
    }
  }

  const isMobileSearchMode = ref(false)

  function gotoDefault() {
    const defaultRegion = localStorage.getItem('MS/region')
    router.replace({
      params: {
        region: rankingRegions.find((x) => x.key === defaultRegion)?.key ?? rankingRegions[0].key,
        type: rankingTypes[0].key,
        filter: rankingFilters[0].key,
      },
      query: { world_type: worldTypes[2].key },
    })
  }

  function reset() {
    router.replace({ params: { region: '', type: '', filter: '', filter2: '' } })
    expandedMenu.value = 'region'
  }

  onMounted(() => {
    if (!mobile.value && !optionSelectionComplete.value) gotoDefault()
    if (!tabletAndMobile.value) gsap.from('.ranking-option', { scale: 0, stagger: 0.05, clearProps: true })
  })
</script>

<style lang="scss" scoped>
  .header-bar {
    position: relative;
    height: 60px;
    width: 100%;
    background: #ff8737;
    font-size: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px 3px 0 0;
    color: white;
    .search-box {
      position: absolute;
      right: 10px;
    }
    .search-button-mobile {
      position: absolute;
      right: 20px;
      color: white;
    }
    h3 {
      font-family: 'Glegoo';
      font-style: normal;
      font-weight: 700;
      margin: 0;
      font-size: 28px;
    }
  }
  .ranking-options-layout {
    display: grid;
    grid-template-columns: 300px auto;
    border: 1px solid var(--border-color);
    border-radius: 0 0 3px 3px;
    border-top: none;

    grid-gap: 1px;
    background-color: var(--border-color);

    > div {
      &:nth-child(2n-1) {
        display: flex;
        align-items: center;
        background-color: #f1f1f1;
        white-space: nowrap;
        padding: 20px 70px;
        text-transform: uppercase;
        font-weight: 700;
      }
      &:nth-child(2n) {
        background-color: #f9f9fb;
      }
    }
    .options {
      display: flex;
      align-items: center;
      // background-color: #f9f9fb;
      padding: 20px;
      flex-wrap: wrap;
      gap: 10px;
    }
  }
  .mobile-reset {
    color: #8e8e8e;
    justify-content: center;
    cursor: pointer;
    &:active {
      color: var(--content-text-color);
    }
  }
  .rankings-header {
    margin: 20px 0 10px;
  }
  .no-option-complete {
    padding: 50px;
    font-size: 28px;
  }

  .dropdown {
    // border: 1px solid red;
    width: 100%;
    padding: 0 4px 0 20px;
    display: flex;
    align-items: center;
    position: relative;
    cursor: pointer;

    .ranking-option {
      opacity: 1;
      transition: opacity 0.3s;
    }

    .dropdown-icon {
      position: absolute;
      right: 0;

      transform: rotate(-90deg);
      transition: transform 0.3s;
      height: 15px;
      // border: 1px solid green;
      :deep(path) {
        fill: #9f9f9f;
      }
    }
    &.expanded {
      .ranking-option {
        opacity: 0;
      }
      .dropdown-icon {
        transform: rotate(90deg);
      }
    }

    &:hover {
      :deep(path) {
        fill: #5a5753;
      }
    }
  }
</style>

<style lang="scss">
  .tablet {
    h3 {
      font-size: min(1rem, 28px) !important;
    }
    .header-bar {
      justify-content: left;
      padding: 0 30px;
    }
    .ranking-options-layout {
      grid-template-columns: 200px auto;
      > div:nth-child(2n-1) {
        padding: 20px 30px;
      }
    }

    .ranking-option {
      &.disabled {
        display: none;
      }
    }
  }
  .mobile {
    .header-bar {
      h3 {
        font-size: 24px;
      }
    }
    .ranking-options-layout {
      display: block !important;
      > div {
        background-color: #f9f9fb !important;
        &:nth-child(2n-1) {
          border-top: 1px solid #dbd9d9;
          padding: 20px;
        }
      }
      .options {
        padding: 0 20px 20px 20px;
      }
    }
    .ranking-option {
      &.disabled {
        display: none;
      }
    }
  }
  // hide inactive options for pc
  .ranking-option {
    &.disabled {
      display: none;
    }
  }
</style>
